import React, { FC, useEffect, useMemo, useState } from 'react'
import { Box, Text, Grid, HStack, Center, useRadioGroup, Flex, Image } from '@chakra-ui/react'
import { RadioButtonProps } from '../radio-buttons/interfaces'
import { selectScenario, useSelector } from '../../state'
import { cardImageMap, ScenarioType } from '../../state/interfaces'
import { useTranslation } from 'react-i18next'

interface Choice {
  value: string
  label: string
  image?: string
  isChecked?: boolean
}

export interface AlertCriteriaProps {
  name: string
  choices: Choice[]
  value?: string
  onChange?: (values: string[]) => void
  component: FC<RadioButtonProps>
  grandImage?: string
}

export const alertCriteria: FC<AlertCriteriaProps> = ({
  choices,
  value,
  onChange,
  name,
  grandImage,
  component: Component,
}) => {
  const { getRadioProps, getRootProps } = useRadioGroup({
    onChange: (value) => onChange && onChange([value]),
    value,
    name,
  })

  const scenario = useSelector(selectScenario)
  const initialInformation = scenario?.initialInformation
  const scenarioType = scenario?.type
  const { t } = useTranslation()
  const [data, setData] = useState(`0 ${t('common_GB')}`)
  const image = useMemo(() => (grandImage ? cardImageMap[grandImage] : ''), [grandImage])

  useEffect(() => {
    if (initialInformation && initialInformation['how-much-data']) {
      if (initialInformation['how-much-data'].match(/\d{3,}(\s)?(mb|Mo)/i)) {
        setData(`${parseInt(initialInformation['how-much-data']) / 1000} ${t('common_GB')}`)
      } else {
        setData(initialInformation['how-much-data'])
      }
    }
  }, [initialInformation])

  return (
    <Box>
      {initialInformation && scenarioType === ScenarioType.MOBILE_ALERT && (
        <>
          <Box
            minWidth={300}
            padding={4}
            border={2}
            borderStyle="solid"
            borderColor="grey"
            borderRadius="xl"
            margin={4}
          >
            <Flex>
              <Image src={image} alt="image" width={['0', '150px']} height={['0', '150px']} />
              <Grid gap={2}>
                <Flex margin="auto" fontWeight="bold">
                  {initialInformation['phone-name']}
                </Flex>
                <Flex mt={4}>
                  <Text as="b">{t('common_calling-minutes')}: </Text>
                  <Text as="i">{initialInformation['how-many-calling-minutes']}</Text>
                </Flex>
                <Flex>
                  <Text as="b">{t('common_data-amount')}: </Text>
                  <Text as="i">{data}</Text>
                </Flex>
                <Flex>
                  <Text as="b">{t('common_options')}: </Text>
                  <Text as="i">{initialInformation['services']}</Text>
                </Flex>
              </Grid>
            </Flex>
          </Box>
          <HStack {...getRootProps()} justifyContent="center" margin="2vh">
            {choices.map((props) => (
              <Component key={`choice-${props.value}`} {...getRadioProps({ value: props.value })} {...props} />
            ))}
          </HStack>
        </>
      )}

      {initialInformation && scenarioType === ScenarioType.INTERNET_ALERT && (
        <>
          <Box
            minWidth={300}
            padding={4}
            border={2}
            borderStyle="solid"
            borderColor="grey"
            borderRadius="xl"
            margin={4}
          >
            <Grid gap={2}>
              <Center margin="auto" mb={2}>
                <Text as="b">{t('criteria')}: </Text>
              </Center>
              <Flex mt={3}>
                <Text as="b">{t('download-speed')}: </Text>
                <Text as="i">{initialInformation['download-speed']}</Text>
              </Flex>
              <Flex>
                <Text as="b">{t('monthly-data-usage')}: </Text>
                <Text as="i">
                  {initialInformation['monthly-usage'].match(/unlimited-data/i) ? (
                    <Text>{t('common_unlimited-data')}</Text>
                  ) : (
                    initialInformation['monthly-usage']
                  )}
                </Text>
              </Flex>
              <Flex>
                <Text as="b">{t('common_location')}: </Text>
                <Text as="i">
                  {initialInformation['postal-code'] !== 'nc'
                    ? initialInformation['postal-code']
                    : initialInformation['locality'] !== 'nc'
                    ? initialInformation['locality']
                    : initialInformation['location']}
                </Text>
              </Flex>
              <Flex>
                <Text as="b">{t('common_options')}: </Text>
                <Text as="i">{initialInformation['options-alert']}</Text>
              </Flex>
            </Grid>
          </Box>
          <HStack {...getRootProps()} justifyContent="center" margin="2vh">
            {choices.map((props) => (
              <Component key={`choice-${props.value}`} {...getRadioProps({ value: props.value })} {...props} />
            ))}
          </HStack>
        </>
      )}

      {!initialInformation && (
        <HStack {...getRootProps()} justifyContent="center" margin="2vh">
          {choices.map((props) => (
            <Component key={`choice-${props.value}`} {...getRadioProps({ value: props.value })} {...props} />
          ))}
        </HStack>
      )}
    </Box>
  )
}
